/**
 * @fileoverview The check-up's waiting state: a calm, progressive list of what Grow is weighing.
 *
 * Each step appears in turn (about every 1.7 seconds), the finished ones ticked and the
 * current one softly pulsing. The last step ("Looking deeper...") holds until the answer
 * arrives; when it does, the remaining steps tick off quickly and the owner is told, so the
 * answer never shows before the list has finished. The steps are announced through a polite
 * live region. Under prefers-reduced-motion nothing pulses or fades in; the steps still appear
 * one by one.
 *
 * Every step is something the check-up really sends (the plant's photos, its notes and stage
 * history, the weather before each photo, the forecast and frost dates, the problem list).
 *
 * The list itself is the shared loader's (GrowSteps, shared/components/ui/GrowLoader), so the
 * Plant Doctor and the rest of the app wait with the same motion.
 *
 * Used by: PlantDoctor (the first look, and the follow-up rounds).
 *
 * Translation keys: plants.diagnose.progress.*
 *
 * @module DoctorProgress
 */

import React from 'react';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { GrowSteps } from '@/shared/components/ui/GrowLoader';

/** Milliseconds between steps while the answer is still on its way. */
const STEP_MS = 1700;

/** Milliseconds between the remaining steps once the answer has arrived. */
const CATCH_UP_MS = 220;

/** Milliseconds to rest on the finished list before handing over to the answer. */
const SETTLE_MS = 400;

/** What DoctorProgress is given. */
interface DoctorProgressProps {
  /** The first look, with every step, or a follow-up round, with a shorter list. */
  kind: 'first' | 'followUp';
  /** The plant's name, used in the first and sixth steps. */
  plantName: string;
  /** Whether the newest photo is recent enough for the forecast to be used. */
  forecastUsed: boolean;
  /** True once the answer has arrived: the remaining steps finish, then onDone is called. */
  ready: boolean;
  /** Called once, after every step is ticked and the list has settled. */
  onDone: () => void;
}

/**
 * The translation keys of a kind's steps, in order.
 *
 * @param kind - The first look or a follow-up round.
 * @param forecastUsed - Whether the forecast is part of the look.
 * @returns The keys under plants.diagnose.progress.
 */
const stepKeys = (kind: 'first' | 'followUp', forecastUsed: boolean): string[] => {
  if (kind === 'followUp') return ['weighing', 'deeper'];
  return ['start', 'photos', 'notes', 'weather', forecastUsed ? 'forecast' : 'frost', 'compare', 'deeper'];
};

/**
 * The progressive "what we are weighing" list shown while a check-up runs.
 *
 * @param props - DoctorProgressProps.
 */
export const DoctorProgress: React.FC<DoctorProgressProps> = ({ kind, plantName, forecastUsed, ready, onDone }) => {
  const { reactTranslate: t } = useTranslation();
  const keys = stepKeys(kind, forecastUsed);
  const total = keys.length;
  // How many steps are showing; total + 1 means every one is ticked.
  const [shown, setShown] = React.useState(1);
  const onDoneRef = React.useRef(onDone);
  onDoneRef.current = onDone;

  React.useEffect(() => {
    if (!ready) {
      // Waiting: advance to the last step and hold there.
      const timer = window.setInterval(() => setShown((n) => Math.min(n + 1, total)), STEP_MS);
      return () => window.clearInterval(timer);
    }
    // Arrived: finish the remaining ticks quickly, then hand over.
    const timer = window.setInterval(() => setShown((n) => Math.min(n + 1, total + 1)), CATCH_UP_MS);
    return () => window.clearInterval(timer);
  }, [ready, total]);

  const finished = ready && shown > total;
  React.useEffect(() => {
    if (!finished) return undefined;
    const timer = window.setTimeout(() => onDoneRef.current(), SETTLE_MS);
    return () => window.clearTimeout(timer);
  }, [finished]);

  return (
    <GrowSteps
      steps={keys.slice(0, Math.min(shown, total)).map((key, index) => ({
        key,
        text: t(`plants.diagnose.progress.${key}`, { plant: plantName }),
        done: index < shown - 1 || finished,
      }))}
    />
  );
};
